import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import JustimmoGuide from '../components/onboarding/JustimmoGuide';
import { toast } from 'sonner';
import { justimmoExportAPI, propertiesAPI } from '../lib/api';
import AppLayout from '../components/AppLayout';
import {
  Building2, Link2, Unlink, RefreshCw, Loader2, CheckCircle2,
  XCircle, Download, ExternalLink, Home, Users, Eye, Filter,
  User as UserIcon, ShieldCheck, AlertCircle
} from 'lucide-react';

export default function JustimmoPage() {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Connection form
  const [apiUsername, setApiUsername] = useState('');
  const [apiPassword, setApiPassword] = useState('');
  
  // Properties
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [importingId, setImportingId] = useState(null);
  
  // Team members (used for both the persistent agent-profile picker
  // and the transient browse filter below).
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('');
  // Persisted agent profile = the `default_owner_id` saved on the
  // connection. Auto-post pipeline reads from this. Distinct from
  // `selectedOwner` (which is just a view filter on this page).
  const [myAgentId, setMyAgentId] = useState('');
  const [savingAgent, setSavingAgent] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await justimmoExportAPI.getStatus();
      setConnectionStatus(res.data);
      // Hydrate the persisted agent profile so the picker reflects what
      // the auto-post pipeline will use.
      const ownerId = res.data?.default_owner_id;
      if (ownerId) {
        setMyAgentId(String(ownerId));
        // Also seed the browse filter so the property list lands scoped
        // to "my listings" by default. Matches the post-onboarding intent.
        setSelectedOwner((prev) => prev || String(ownerId));
      }
    } catch (err) {
      setConnectionStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProperties = useCallback(async (newOffset = 0) => {
    if (!connectionStatus?.connected) return;
    
    setPropertiesLoading(true);
    try {
      const params = {
        limit: 20,
        offset: newOffset,
        only_active: true,
      };
      if (selectedOwner) {
        params.owner_id = selectedOwner;
      }
      
      const res = await justimmoExportAPI.listProperties(params);
      setProperties(res.data.properties || []);
      setTotalCount(res.data.total_count || 0);
      setOffset(newOffset);
    } catch (err) {
      toast.error('Failed to load properties');
    } finally {
      setPropertiesLoading(false);
    }
  }, [connectionStatus?.connected, selectedOwner]);

  const loadTeam = useCallback(async () => {
    if (!connectionStatus?.connected) return;
    
    try {
      const res = await justimmoExportAPI.getTeam();
      setTeamMembers(res.data.team_members || []);
    } catch (err) {
      // Silent: team-member list is a nice-to-have filter. The page still
      // functions without it. 401 redirects via the global axios interceptor.
    }
  }, [connectionStatus?.connected]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (connectionStatus?.connected) {
      loadProperties(0);
      loadTeam();
    }
  }, [connectionStatus?.connected, loadProperties, loadTeam]);

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!apiUsername || !apiPassword) {
      toast.error('Please enter both username and password');
      return;
    }
    
    setConnecting(true);
    try {
      const res = await justimmoExportAPI.connect({
        api_username: apiUsername,
        api_password: apiPassword,
        auto_import_new: true,
      });
      
      toast.success(`Connected! Found ${res.data.total_properties} properties`);
      setApiUsername('');
      setApiPassword('');
      loadStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to connect';
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Justimmo?')) return;
    
    try {
      await justimmoExportAPI.disconnect();
      toast.success('Disconnected from Justimmo');
      setConnectionStatus({ connected: false });
      setProperties([]);
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const handleImport = async (property) => {
    setImportingId(property.justimmo_id);
    try {
      const res = await justimmoExportAPI.importProperty(property.justimmo_id);
      
      if (res.data.already_exists) {
        toast.info('Property already imported');
      } else {
        toast.success(`Imported: ${res.data.title}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to import property';
      toast.error(msg);
    } finally {
      setImportingId(null);
    }
  };

  const handleOwnerFilter = (ownerId) => {
    setSelectedOwner(ownerId);
    setOffset(0);
  };

  const handleChangeMyAgent = async (e) => {
    const value = e.target.value;
    // Allow empty (clear-and-reset). Backend interprets "" as null.
    setMyAgentId(value);
    setSavingAgent(true);
    try {
      const res = await justimmoExportAPI.updateConnection({
        default_owner_id: value,
      });
      const backfilled = res.data?.backfilled_properties || 0;
      if (value) {
        toast.success(
          backfilled > 0
            ? `Profil gespeichert. ${backfilled} bestehende Immobilien wurden dir zugeordnet.`
            : 'Profil gespeichert. Auto-Posts nutzen ab jetzt deine Listings.'
        );
        // Sync the browse filter so the property list re-scopes to the
        // newly-picked agent.
        setSelectedOwner(value);
      } else {
        toast.info('Profil zurückgesetzt. Auto-Posts pausiert, bis du wieder einen Makler wählst.');
      }
      // Refresh status so default_owner_id displayed elsewhere stays in sync.
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Konnte dein Profil nicht speichern.');
      // Revert optimistic state on failure.
      setMyAgentId(connectionStatus?.default_owner_id ? String(connectionStatus.default_owner_id) : '');
    } finally {
      setSavingAgent(false);
    }
  };

  useEffect(() => {
    if (connectionStatus?.connected) {
      loadProperties(0);
    }
  }, [selectedOwner, connectionStatus?.connected]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="justimmo-page" className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#23211a]">Justimmo Import</h1>
          <p className="text-sm text-[#8a8474] mt-0.5">
            Connect your Justimmo account to import property listings
          </p>
        </div>
        {connectionStatus?.connected && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadStatus(); loadProperties(0); }}
            className="border-[#d8d0bd] text-[#6f6a5c] text-xs"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        )}
      </div>

      {/* Connection Status Card */}
      <Card className="glass border-[#e3dccb]">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-[#23211a] flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#1f4d3a]" />
            Justimmo Export API
          </CardTitle>
          <CardDescription>
            Connect using your Export API credentials from Justimmo admin panel
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connectionStatus?.connected ? (
            <div className="space-y-4">
              <div className={`flex items-center justify-between p-4 rounded-lg ${
                connectionStatus.total_properties > 0 
                  ? 'bg-[#e4f0e8] border border-[#1e7a4f]/40' 
                  : 'bg-[#f6edd8] border border-[#c7a869]'
              }`}>
                <div className="flex items-center gap-3">
                  {connectionStatus.total_properties > 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-[#1e7a4f]" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-[#9a6d1f]" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      connectionStatus.total_properties > 0 ? 'text-[#1e7a4f]' : 'text-[#9a6d1f]'
                    }`}>
                      Connected
                    </p>
                    <p className="text-xs text-[#6f6a5c]">
                      User: {connectionStatus.api_username} • 
                      {connectionStatus.total_properties > 0 
                        ? ` ${connectionStatus.total_properties} properties available`
                        : ' No properties in Justimmo yet'
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDisconnect}
                  className="text-[#b3543f] hover:text-[#a34430] hover:bg-[#f7e8e3]"
                  data-testid="disconnect-btn"
                >
                  <Unlink className="w-4 h-4 mr-1" /> Disconnect
                </Button>
              </div>
              
              {connectionStatus.last_sync_at && (
                <p className="text-xs text-[#8a8474]">
                  Last sync: {new Date(connectionStatus.last_sync_at).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleConnect} className="space-y-4">
              {/* Same illustrated 8-step guide we use in the onboarding
                  wizard so users re-visiting their Justimmo settings can
                  re-rotate creds without hunting for the instructions. */}
              <JustimmoGuide />


              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="api-username" className="text-[#4a463b]">API Username</Label>
                  <Input
                    id="api-username"
                    type="text"
                    value={apiUsername}
                    onChange={(e) => setApiUsername(e.target.value)}
                    placeholder="Enter API username"
                    className="bg-[#fffdf7] border-[#d8d0bd] text-[#23211a]"
                    data-testid="api-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="api-password" className="text-[#4a463b]">API Password</Label>
                  <Input
                    id="api-password"
                    type="password"
                    value={apiPassword}
                    onChange={(e) => setApiPassword(e.target.value)}
                    placeholder="Enter API password"
                    className="bg-[#fffdf7] border-[#d8d0bd] text-[#23211a]"
                    data-testid="api-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={connecting}
                className="w-full bg-[#1f4d3a] hover:bg-[#1f4d3a]"
                data-testid="connect-btn"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" /> Connect to Justimmo
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Mein Profil. Persisted agent picker. This is what the auto-post
          pipeline reads to decide which Justimmo listings count as "yours".
          Distinct from the transient browse filter on the property list
          card below: changing this also changes auto-post scoping. */}
      {connectionStatus?.connected && (
        <Card className="glass border-[#e3dccb]">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-[#23211a] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#1e7a4f]" />
              Mein Profil in der Agentur
            </CardTitle>
            <CardDescription>
              Auto-Posts nutzen ausschließlich Immobilien des hier gewählten Maklers. Niemals die deiner Kollegen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {teamMembers.length === 0 ? (
              <div className="flex items-start gap-2 text-xs text-[#9a6d1f] bg-[#f6edd8] border border-[#c7a869] rounded-md p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Wir konnten die Mitarbeiterliste nicht laden. Stelle sicher, dass dein Justimmo-API-Account die
                  &nbsp;<code className="text-[#9a6d1f]">team/list</code>-Berechtigung hat.
                </span>
              </div>
            ) : (
              <>
                <div className="relative">
                  <select
                    value={myAgentId}
                    onChange={handleChangeMyAgent}
                    disabled={savingAgent}
                    className="w-full text-sm bg-[#fffdf7] border border-[#d8d0bd] rounded-md pl-9 pr-8 py-2 text-[#23211a] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
                    data-testid="agent-profile-select"
                  >
                    <option value="">– Kein Makler gewählt (Auto-Post pausiert) –</option>
                    {teamMembers.map((member) => {
                      const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
                      return (
                        <option key={member.id} value={member.id}>
                          {fullName || `Mitarbeiter #${member.id}`}
                          {member.position ? ` · ${member.position}` : ''}
                          {member.category ? ` · ${member.category}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  <UserIcon className="w-4 h-4 text-[#8a8474] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  {savingAgent && (
                    <Loader2 className="w-4 h-4 animate-spin text-[#6f6a5c] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </div>
                {myAgentId ? (
                  <p className="text-xs text-[#8a8474]">
                    {(() => {
                      const me = teamMembers.find((m) => String(m.id) === String(myAgentId));
                      if (!me) return 'Profil aktiv.';
                      const fullName = [me.first_name, me.last_name].filter(Boolean).join(' ').trim();
                      return `Aktuell: ${fullName || `#${me.id}`}${me.email ? ` · ${me.email}` : ''}`;
                    })()}
                  </p>
                ) : (
                  <p className="text-xs text-[#9a6d1f]">
                    Solange kein Profil gewählt ist, werden keine Auto-Posts erstellt.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Properties List */}
      {connectionStatus?.connected && (
        <Card className="glass border-[#e3dccb]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#23211a] flex items-center gap-2">
                <Home className="w-5 h-5 text-[#1f4d3a]" />
                Available Properties
                <span className="text-sm font-normal text-[#8a8474]">({totalCount} total)</span>
              </CardTitle>
              
              {/* Owner Filter */}
              {teamMembers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-[#8a8474]" />
                  <select
                    value={selectedOwner}
                    onChange={(e) => handleOwnerFilter(e.target.value)}
                    className="text-xs bg-[#fffdf7] border border-[#d8d0bd] rounded px-2 py-1 text-[#4a463b]"
                    data-testid="owner-filter"
                  >
                    <option value="">All Agents</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {propertiesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#1f4d3a] animate-spin" />
              </div>
            ) : properties.length === 0 ? (
              <div className="text-center py-12">
                <Home className="w-12 h-12 mx-auto mb-3 text-[#9c9585]" />
                <p className="text-[#6f6a5c] font-medium">No properties found in Justimmo</p>
                <p className="text-xs text-[#8a8474] mt-2 max-w-md mx-auto">
                  Your Justimmo account doesn't have any properties yet. 
                  Add properties in Justimmo and they will appear here automatically.
                </p>
                {teamMembers.length > 0 && (
                  <div className="mt-6 p-4 bg-[#efe9db] border border-[#d8d0bd] rounded-lg max-w-md mx-auto text-left">
                    <p className="text-xs text-[#6f6a5c] mb-2">Team members found ({teamMembers.length}):</p>
                    <div className="space-y-1">
                      {teamMembers.slice(0, 5).map((member) => (
                        <div key={member.id} className="flex items-center gap-2 text-xs text-[#4a463b]">
                          {member.image ? (
                            <img src={member.image} alt="" className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-[#d8d0bd] flex items-center justify-center">
                              <Users className="w-3 h-3 text-[#8a8474]" />
                            </div>
                          )}
                          <span>{member.first_name} {member.last_name}</span>
                          <span className="text-[#8a8474]">• {member.position}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {properties.map((prop) => (
                    <div
                      key={prop.justimmo_id}
                      className="flex items-center gap-4 p-4 bg-[#f2ecdf] border border-[#d8d0bd] rounded-lg hover:bg-[#efe9db] transition-colors"
                      data-testid={`property-${prop.justimmo_id}`}
                    >
                      {/* Thumbnail */}
                      <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden bg-[#d8d0bd]">
                        {prop.images?.[0] ? (
                          <img
                            src={prop.images[0]}
                            alt={prop.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Home className="w-8 h-8 text-[#8a8474]" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-[#23211a] truncate">
                          {prop.title || `Property #${prop.objektnummer}`}
                        </h4>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs text-[#6f6a5c]">
                            {prop.city}, {prop.zip_code}
                          </span>
                          {prop.rooms && (
                            <span className="text-xs text-[#8a8474]">• {prop.rooms} rooms</span>
                          )}
                          {prop.living_area && (
                            <span className="text-xs text-[#8a8474]">• {prop.living_area} m²</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {prop.is_rent && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                              Rent {prop.total_rent && `€${prop.total_rent}/mo`}
                            </span>
                          )}
                          {prop.is_purchase && (
                            <span className="text-xs px-1.5 py-0.5 bg-[#d6e8dd] text-[#1e7a4f] rounded">
                              Buy {prop.price && `€${prop.price}`}
                            </span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            prop.status === 'aktiv' 
                              ? 'bg-[#1e7a4f]/20 text-[#1e7a4f]' 
                              : 'bg-[#e9e2d2] text-[#6f6a5c]'
                          }`}>
                            {prop.status}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleImport(prop)}
                          disabled={importingId === prop.justimmo_id}
                          className="border-[#1f4d3a]/30 text-[#1f4d3a] hover:bg-[#e9efe7]"
                          data-testid={`import-btn-${prop.justimmo_id}`}
                        >
                          {importingId === prop.justimmo_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" /> Import
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination */}
                {totalCount > 20 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#d8d0bd]">
                    <p className="text-xs text-[#8a8474]">
                      Showing {offset + 1}-{Math.min(offset + properties.length, totalCount)} of {totalCount}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadProperties(Math.max(0, offset - 20))}
                        disabled={offset === 0 || propertiesLoading}
                        className="border-[#d8d0bd] text-[#6f6a5c]"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadProperties(offset + 20)}
                        disabled={offset + 20 >= totalCount || propertiesLoading}
                        className="border-[#d8d0bd] text-[#6f6a5c]"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="glass border-[#e3dccb]">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#e9efe7]">
              <Building2 className="w-5 h-5 text-[#1f4d3a]" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-[#4a463b]">About Justimmo Integration</h4>
              <p className="text-xs text-[#8a8474] mt-1">
                This integration uses the Justimmo Export API to fetch your property listings.
                Once imported, you can generate AI-powered videos and captions for your social media posts.
                We recommend enabling "auto-import new properties" to automatically sync new listings.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </AppLayout>
  );
}
